import { HeartFilled, StarFilled, TrophyFilled } from '@ant-design/icons';
import { Avatar, Card, Form, Input, Select, Button } from 'antd';
import { createContactSalesLead } from '@/api/contactSales';
import { message } from 'antd';
import { testimonials, businessTypes } from '../../constants';
import React, { useState, useEffect } from 'react';
import { allCountries } from '../../constants/allCountries';
import { CONTACT_SALES_CONSTANTS, ROLES, WHITELABEL_URL } from '../../constants/contactSales';

import { PUBLIC_EMAIL_DOMAINS } from 'src/constants/publicEmailDomains';

const { Option } = Select;
const RegisterAi = () => {
	const [form] = Form.useForm();
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showCalendly, setShowCalendly] = useState(false);
	const [role, setRole] = useState(null);
	useEffect(() => {
		if (role === CONTACT_SALES_CONSTANTS.SERVICE_PROVIDER) {
			window.location.href = WHITELABEL_URL;
		}
	}, [role]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setHasSubmitted(
				localStorage.getItem(CONTACT_SALES_CONSTANTS.STORAGE_KEYS.SUBMITTED) === 'true',
			);
			setShowCalendly(
				localStorage.getItem(CONTACT_SALES_CONSTANTS.STORAGE_KEYS.SHOW_CALENDLY) === 'true',
			);
		}
	}, []);

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const response = await createContactSalesLead(values);
			message.success('Your demo request has been submitted!');
			form.resetFields();
			const shouldShowCalendly = response?.data?.redirectToCalendly || false;
			setShowCalendly(shouldShowCalendly);
			setHasSubmitted(true);

			if (typeof window !== 'undefined') {
				localStorage.setItem(CONTACT_SALES_CONSTANTS.STORAGE_KEYS.SUBMITTED, 'true');
				localStorage.setItem(
					CONTACT_SALES_CONSTANTS.STORAGE_KEYS.SHOW_CALENDLY,
					shouldShowCalendly ? 'true' : 'false',
				);
			}
		} catch (error) {
			message.error(error.message || 'Submission failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 business-details-step-v2 relative overflow-hidden">
			<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
				<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
					<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-indigo-200 flex items-center justify-center">
						<img src={'new_logo.png'} alt="" className="rounded-lg w-6 h-6" />
					</div>
				</div>
			</div>

			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
				<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
			</div>

			<div className="min-h-screen flex w-full relative z-10">
				<div className="hidden lg:flex lg:w-1/2 flex-col justify-center relative">
					<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900" />
					<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

					<div className="relative z-10 max-w-lg mx-auto p-6">
						<div className="mb-8 text-center">
							<div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
								<TrophyFilled className="text-yellow-400 text-sm" />
								<span className="text-white text-xs font-medium">Trusted by 1,000+ businesses</span>
							</div>
							<h2 className="text-3xl font-bold text-white mb-3 leading-tight">
								<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
									Double your revenue
								</span>{' '}
								with Frontdesk.
							</h2>
						</div>

						<div className="space-y-4">
							{testimonials.map((testimonial, index) => (
								<div
									key={testimonial.id}
									className="group bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:scale-105 transition-all duration-300 transform"
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									<div className="flex mb-2">
										{[...Array(testimonial.rating)].map((_, i) => (
											<StarFilled key={i} className="text-yellow-400 text-sm" />
										))}
									</div>
									<blockquote className="text-blue-50 text-xs leading-relaxed mb-3 italic">
										"{testimonial.content}"
									</blockquote>
									<div className="flex items-center gap-3">
										<div className="relative">
											<Avatar
												src={testimonial.avatar}
												size={36}
												className="border-2 border-white/30"
											/>
										</div>
										<div className="flex flex-col">
											<h4 className="font-semibold text-white text-xs m-0">{testimonial.name}</h4>
											<p className="text-blue-200 text-xs m-0 flex items-center gap-1">
												<HeartFilled className="text-red-400 text-xs" />
												{testimonial.business}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
					<div className="w-full">
						{hasSubmitted ? (
							showCalendly ? (
								<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden p-6 flex flex-col items-center justify-center mt-8">
									<div className="w-20 h-20 flex items-center justify-center bg-blue-50 rounded-full mx-auto mb-4">
										<span role="img" aria-label="calendar" className="text-blue-500 text-4xl">
											📅
										</span>
									</div>
									<h1 className="text-3xl font-bold mb-2 text-center">
										<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
											Book Your Onboarding Call
										</span>{' '}
									</h1>
									<p className="text-gray-500 text-sm text-center mb-4">
										You qualify for instant onboarding. Please schedule your call below.
									</p>
									<a
										href={CONTACT_SALES_CONSTANTS.CALENDLY_URLS.QUALIFIED}
										rel="noreferrer"
										target="_blank"
									>
										<Button className="w-full my-7" type="primary" size="large">
											<span className="text-white font-semibold">Schedule Your Meeting</span>
										</Button>
									</a>
									<p className="text-gray-500 text-center">
										Our team looks forward to meeting you!
									</p>
								</Card>
							) : (
								<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden p-6 flex flex-col items-center justify-center mt-8">
									<div className="w-20 h-20 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
										<span role="img" aria-label="check" className="text-green-500 text-4xl">
											🎉
										</span>
									</div>
									<h1 className="text-3xl font-bold mb-2 text-center">
										<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
											Congratulations!
										</span>{' '}
									</h1>
									<p className="text-gray-500 text-sm text-center mb-4">
										Thank you for your interest!
									</p>
									<div className="rounded-2xl bg-blue-50 p-4 text-center mb-4">
										<span role="img" aria-label="calendar" className="text-blue-500 text-3xl">
											📅
										</span>
										<h2 className="text-lg font-bold text-blue-500 mb-2 mt-2">
											Our team will contact you shortly
										</h2>
										<p className="text-gray-500 text-sm">
											We appreciate your submission. A member of our team will reach out to you soon
											to discuss your needs and next steps.
										</p>
									</div>
								</Card>
							)
						) : (
							<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
								<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />
								<div className="p-6">
									<div className="mb-6 text-center">
										<h2 className="text-3xl font-bold mb-1 text-blue-900">Book Your Free Demo</h2>
										<p className="text-gray-500 text-sm mb-0">
											See how our AI receptionist can transform your business in just 15 minutes.
										</p>
									</div>
									<Form
										form={form}
										layout="vertical"
										onFinish={onFinish}
										style={{ margin: 'auto' }}
									>
										<Form.Item
											name="role"
											label="Role"
											rules={[{ required: true, message: 'Please select your role' }]}
										>
											<Select
												placeholder="Select your role"
												className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
												style={{ height: '48px' }}
												onChange={(value) => setRole(value)}
											>
												{ROLES.map((role) => (
													<Option key={role.value} value={role.value}>
														{role.label}
													</Option>
												))}
											</Select>
										</Form.Item>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-4">
											<Form.Item
												name="firstName"
												label="First Name"
												rules={[{ required: true, message: 'Please enter your first name' }]}
											>
												<Input
													placeholder="Enter your first name"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>
											<Form.Item
												name="lastName"
												label="Last Name"
												rules={[{ required: true, message: 'Please enter your last name' }]}
											>
												<Input
													placeholder="Enter your last name"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-4">
											<Form.Item
												name="email"
												label="Business Email"
												rules={[
													{ required: true, message: 'Please enter your email' },
													{ type: 'email', message: 'Enter a valid email' },
													{
														validator: (_, value) => {
															if (!value) {
																return Promise.resolve();
															}

															const domain = value.split('@')[1]?.toLowerCase();
															if (PUBLIC_EMAIL_DOMAINS.has(domain)) {
																return Promise.reject(
																	new Error(
																		'Please use your business email address (not a public email).',
																	),
																);
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<Input
													placeholder="Enter your email"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>

											<Form.Item
												name="phone"
												label="Phone Number"
												rules={[{ required: true, message: 'Please enter your phone number' }]}
											>
												<Input
													placeholder="Enter phone number with country code"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-4">
											<Form.Item
												name="businessName"
												label="Business Name"
												rules={[{ required: true, message: 'Please enter your business name' }]}
											>
												<Input
													placeholder="Enter your business name"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>
											<Form.Item
												name="businessType"
												label="Business Type"
												rules={[{ required: true, message: 'Please select your business type' }]}
											>
												<Select
													placeholder="Select business type"
													className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													style={{ height: '48px' }}
												>
													{businessTypes.map((type) => (
														<Option key={type.id} value={type.value || type.id}>
															{type.label}
														</Option>
													))}
												</Select>
											</Form.Item>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-4">
											<Form.Item
												name="country"
												label="Country/Region"
												rules={[{ required: true, message: 'Please select your country' }]}
											>
												<Select
													showSearch
													placeholder="Select country"
													optionFilterProp="children"
													className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													style={{ height: '48px' }}
												>
													{allCountries.map((country) => (
														<Option key={country.code} value={country.name}>
															{country.name}
														</Option>
													))}
												</Select>
											</Form.Item>
											<Form.Item
												name="companySize"
												label="Company Size"
												rules={[{ required: true, message: 'Please select a company size' }]}
											>
												<Select
													placeholder="Please select one"
													className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													style={{ height: '48px' }}
												>
													<Option value="1 - 5">1 - 5</Option>
													<Option value="5 - 15">5 - 15</Option>
													<Option value="16 - 50">16-50</Option>
													<Option value="50 - 200">50-200</Option>
													<Option value="200 - 500">200 - 500</Option>
													<Option value="500+">500+</Option>
												</Select>
											</Form.Item>
										</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-4">
											<Form.Item
												name="dailyCalls"
												label="Average Daily Calls"
												rules={[{ required: true, message: 'Please select daily calls' }]}
											>
												<Select
													placeholder="Select daily call range"
													className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													style={{ height: '48px' }}
												>
													<Option value="1-10">1-10</Option>
													<Option value="11-25">11–25</Option>
													<Option value="26-50">26-50</Option>
													<Option value="51-100">51-100</Option>
													<Option value="100+">100+</Option>
												</Select>
											</Form.Item>
											<Form.Item
												name="source"
												label="How did you hear about us?"
												rules={[{ required: true, message: 'Please select an option' }]}
											>
												<Select
													placeholder="Select an option"
													className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
													style={{ height: '48px' }}
												>
													<Option value="google">Google</Option>
													<Option value="facebook">Facebook</Option>
													<Option value="linkedin">LinkedIn</Option>
													<Option value="referral">Referral</Option>
													<Option value="other">Other</Option>
												</Select>
											</Form.Item>
										</div>
										<Form.Item
											name="hasTriedProduct"
											label="Have you tried the product?"
											rules={[{ required: true, message: 'Please select an option' }]}
										>
											<Select
												placeholder="Select an option"
												className="rounded-xl border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
												style={{ height: '48px' }}
											>
												<Option value="yes">Yes</Option>
												<Option value="no">No</Option>
											</Select>
										</Form.Item>
										<Form.Item name="notes" label="Notes">
											<Input.TextArea
												placeholder="Add any additional information or questions (optional)"
												rows={3}
												className="rounded-xl border-2 border-gray-200 hover:border-orange-400 focus:border-orange-500 transition-all duration-200 bg-gray-50/50"
											/>
										</Form.Item>
										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												block
												loading={loading}
												disabled={loading}
												className="w-full h-12 rounded-xl font-bold text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
											>
												Book My Free Demo
											</Button>
										</Form.Item>
									</Form>
								</div>
							</Card>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default RegisterAi;
